import {
  CampaignActivityType,
  CampaignAppearanceType,
  CampaignExampleFileInput,
  CampaignSmPlatform,
  CampaignTaskType,
  CampaignUsageRightsScope,
  CbDueDateType,
  CreateCampaignTaskV2Input,
  CustomBriefActivityDetailsInput,
  SearchCustomCriteriaInput,
  StoreGender,
  UpdateCustomCampaignInput,
  UpdateDirectBookingCampaignInput,
  UserRole,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
  CampaignLocationInput,
  CustomBriefProductInput,
} from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity,
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2,
  GetCampaignsV2_getCampaigns_entities_search_CustomSearchCriteria,
} from 'api/campaignV2/types/GetCampaignsV2';
import { TimeLineDeliverable } from 'helpers/deliverables';
// Helpers
import { transformTasksV2ForUpdate } from 'helpers/timelineDeliverables';
import {
  mapUgcEditingDeliverableToFormData,
  mapUgcDeliverableFormDataToTaskInput,
} from 'helpers/campaignUgc';
import { getLocationsForApi } from 'helpers/campaignEditModal';
// Utils
import {
  CustomDeliverableCategory,
  PlatformCriteriaItem,
  PromoType,
} from 'helpers/customCampaignConstants';

export const getCustomSearch = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_search_CustomSearchCriteria | null =>
  record?.search?.__typename === 'CustomSearchCriteria' ? record.search : null;

export const getCategory = (task: {
  type?: CampaignTaskType | null;
}): CustomDeliverableCategory => {
  if (task.type === CampaignTaskType.APPEARANCE) return 'Appearance';
  if (
    task.type === CampaignTaskType.UGC ||
    task.type === CampaignTaskType.CONTENT
  )
    return 'UGC';
  return 'Social Media';
};

type HashtagOption = { value: string; label?: string };

export type CustomCampaignTalentAgeRange = {
  minAge: number;
  maxAge: number | null;
};

export type CustomCampaignFormValues = {
  name: string;
  internalName: string;
  isPrivate: boolean;
  brandName?: string | null;
  website?: string | null;
  instructions?: string | null;
  encouraged?: string | null;
  avoid?: string | null;
  flatFeePerStore?: number | null;
  budget?: number | null;
  reserveBudget?: boolean;
  applicationDeadlineDays?: number | null;
  usageRights: boolean;
  usageRightsText?: string | null;
  usageRightsDurationMonths?: number | null;
  usageRightsScopes?: CampaignUsageRightsScope[] | null;
  promoType?: PromoType | null;
  promoValue?: string | null;
  products?: CustomBriefProductInput[] | null;
  questions: string[];
  // Talent / search filters
  userRole?: UserRole[] | null;
  sports?: string[] | null;
  genders?: (StoreGender | string)[] | string | null;
  ageRanges?: CustomCampaignTalentAgeRange[] | null;
  influencerCategoryIds?: number[] | null;
  locations?: CampaignLocationInput[] | null;
  hometownLocations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  searchHashtags?: HashtagOption[] | null;
  excludePreviouslyWorkedWith?: boolean;
  excludePreviouslyDeclined?: boolean;
  // Audience filters
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceInterests?: AudienceInterest[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
};

export type CustomCampaignValidationValues = {
  name?: string | null;
  internalName?: string | null;
  brandName?: string | null;
  website?: string | null;
  instructions?: string | null;
  encouraged?: string | null;
  avoid?: string | null;
  flatFeePerStore?: number | string | null;
  budget?: number | string | null;
  requireBudget?: boolean;
  applicationDeadlineDays?: number | string | null;
  usageRights?: boolean;
  usageRightsText?: string | null;
  usageRightsDurationMonths?: number | string | null;
  usageRightsScopes?: CampaignUsageRightsScope[] | null;
  questions?: string[] | null;
  isPrivate?: boolean;
  sports?: string[] | null;
  locations?: CampaignLocationInput[] | null;
  ageRanges?: CustomCampaignTalentAgeRange[] | null;
  platformCriteria?: PlatformCriteriaItem[] | null;
  deliverables: TimeLineDeliverable[];
};

export type CampaignTaskExampleFile = CampaignExampleFileInput & {
  url?: string | null;
  name?: string | null;
};

export type CustomCampaignDeliverable = TimeLineDeliverable & {
  examples?: (CampaignTaskExampleFile | File)[];
};

export type AnyTask =
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2;

export type ExampleFile =
  | {
      key: string;
      url?: string | null;
      name?: string | null;
    }
  | File;

export type ExampleFiles = {
  url: string | null;
  key: string;
  file: File | undefined;
};

export type PendingExampleFile = File & { preview?: string };

export type CustomCampaignsEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
  isOpen?: boolean;
};

export const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeGenders = (
  genders: CustomCampaignFormValues['genders']
): StoreGender[] => {
  if (!genders) return [];
  const arr = Array.isArray(genders) ? genders : [genders];
  return arr.filter(Boolean) as StoreGender[];
};

type BuildCustomCriteriaArgs = {
  values: CustomCampaignFormValues;
  platformCriteria: PlatformCriteriaItem[];
};

// Builds SearchCustomCriteriaInput from the live form values. Custom search uses
// per-platform platformCriteria instead of the legacy smPlatforms/follower fields.
export const buildCustomCriteriaInput = ({
  values,
  platformCriteria,
}: BuildCustomCriteriaArgs): SearchCustomCriteriaInput => {
  const genders = normalizeGenders(values.genders);
  const searchLocations = getLocationsForApi(values.locations || []);
  const hometownLocations = getLocationsForApi(values.hometownLocations || []);
  const excludedLocations = getLocationsForApi(values.excludedLocations || []);
  const audienceLocations = getLocationsForApi(values.audienceLocations || []);
  const ageRanges = (values.ageRanges || [])
    .filter(
      (range) =>
        Number.isFinite(range.minAge) &&
        range.minAge >= 0 &&
        (range.maxAge === null ||
          (Number.isFinite(range.maxAge) && range.maxAge >= range.minAge))
    )
    .map((range) => ({
      minAge: range.minAge,
      maxAge: range.maxAge,
    }));

  const criteria: SearchCustomCriteriaInput = {
    roles: values.userRole?.length
      ? values.userRole
      : [UserRole.Athlete, UserRole.ContentCreator],
    ...(genders.length && { genders }),
    ...(values.sports?.length && { sports: values.sports }),
    ...(ageRanges.length && { ageRanges }),
    ...(values.influencerCategoryIds?.length && {
      influencerCategoryIds: values.influencerCategoryIds,
    }),
    excludePreviouslyWorkedWith: Boolean(values.excludePreviouslyWorkedWith),
    excludePreviouslyDeclined: Boolean(values.excludePreviouslyDeclined),
    ...(toNullableNumber(values.flatFeePerStore) != null && {
      flatFeePerStore: toNullableNumber(values.flatFeePerStore) as number,
    }),
    ...(searchLocations.length && { locations: searchLocations }),
    ...(hometownLocations.length && { hometownLocations }),
    ...(excludedLocations.length && { excludedLocations }),
  };

  if (platformCriteria.length) {
    criteria.platformCriteria = platformCriteria.map((item) => ({
      platform: item.platform as CampaignSmPlatform,
      followersRange: {
        ...(item.minFollowers != null && { from: item.minFollowers }),
        ...(item.maxFollowers != null && { to: item.maxFollowers }),
      },
      engagementRateRange: {
        ...(item.minEngagementRate != null && { from: item.minEngagementRate }),
      },
    }));
  }

  if (values.searchHashtags?.length) {
    criteria.hashtagInputs = values.searchHashtags.map((tag) => ({
      name: (tag.label ?? tag.value).trim(),
    }));
  }

  // Audience filters — only forward when something is set.
  const audienceFilters = {
    ...(values.audienceGender && { gender: values.audienceGender }),
    ...(values.audienceAgeRanges?.length && {
      ageRanges: values.audienceAgeRanges,
    }),
    ...(values.audienceEthnicities?.length && {
      ethnicities: values.audienceEthnicities,
    }),
    ...(values.audienceInterests?.length && {
      interests: values.audienceInterests,
    }),
    ...(audienceLocations.length && { locations: audienceLocations }),
  };

  if (Object.keys(audienceFilters).length) {
    criteria.audienceFilters = audienceFilters;
  }

  return criteria;
};

// Custom campaigns mix social-media, UGC, and APPEARANCE tasks. The generic
// social transform (transformTasksV2ForUpdate) does not handle APPEARANCE and
// leaves empty content-date config that the backend rejects. This adapts each
// task: APPEARANCE tasks map to the appearance input; non-appearance tasks reuse
// the social transform but drop content-date fields that have no value.
const transformCustomTasks = (
  tasks: CustomCampaignDeliverable[]
): CreateCampaignTaskV2Input[] => {
  return tasks
    .filter((task: any) => task.type !== CampaignTaskType.FINAL_CONTENT)
    .map((task: any): CreateCampaignTaskV2Input => {
      if (task.type === CampaignTaskType.APPEARANCE) {
        // Appearance details may arrive either nested under `appearance`
        // (from the appearance modal) or flat (from the GraphQL task shape).
        const nested = task.appearance ?? {};
        const fixedDate =
          nested.appearanceDate ??
          task.dueDate?.fixedDate ??
          task.postFixedDate ??
          null;

        return {
          type: CampaignTaskType.APPEARANCE,
          title: task.title ?? '',
          description: task.description ?? null,
          appearance: {
            appearanceType:
              (nested.appearanceType as CampaignAppearanceType) ??
              (task.appearanceType as CampaignAppearanceType) ??
              null,
            appearanceDate: fixedDate ?? null,
            durationText: nested.durationText ?? task.durationText ?? null,
            location: nested.location ?? task.location ?? null,
            travelIncluded: Boolean(
              nested.travelIncluded ?? task.travelIncluded
            ),
            accommodationIncluded: Boolean(
              nested.accommodationIncluded ?? task.accommodationIncluded
            ),
            maxExpenses: nested.maxExpenses ?? task.maxExpenses ?? null,
          },
        };
      }

      if (task.type === CampaignTaskType.UGC) {
        return addTaskExamples(buildUgcTaskInput(task), task);
      }

      const [transformed] = transformTasksV2ForUpdate([task]);

      // The backend rejects empty strings on optional text fields (e.g.
      // `suggestedCaption must be longer than or equal to 1 characters`).
      // New deliverables built from the form carry '' for fields the admin
      // left blank, so drop blank optional strings before sending.
      return addTaskExamples(
        stripInvalidDueDateFields(stripBlankOptionalStrings(transformed)),
        task
      );
    });
};

const addTaskExamples = (
  input: CreateCampaignTaskV2Input,
  task: CustomCampaignDeliverable
): CreateCampaignTaskV2Input => {
  const examples = task.examples
    ?.filter(
      (example): example is CampaignTaskExampleFile =>
        !(example instanceof File) && Boolean(example.key)
    )
    .map((example) => ({ key: example.key }));

  const taskInput = { ...input };
  delete taskInput.examples;

  return examples?.length ? { ...taskInput, examples } : taskInput;
};

const buildUgcTaskInput = (task: any): CreateCampaignTaskV2Input => {
  // Normalize both the freshly-edited local shape and the loaded GraphQL
  // CampaignUgcTask (due date + media type nested on contentItems) into the
  // multi-piece form model, then map it back to a UGC task input. This keeps
  // every content piece and the shared due date intact.
  const formData = mapUgcEditingDeliverableToFormData(task);
  const input = mapUgcDeliverableFormDataToTaskInput(formData);

  return stripInvalidDueDateFields(stripBlankOptionalStrings(input));
};

// Optional text fields the backend validates as min-length 1 when present.
// Sending '' triggers a 400, so blank values must be omitted.
const OPTIONAL_TEXT_TASK_FIELDS: (keyof CreateCampaignTaskV2Input)[] = [
  'suggestedCaption',
  'title',
  'description',
];

const stripBlankOptionalStrings = (
  task: CreateCampaignTaskV2Input
): CreateCampaignTaskV2Input => {
  const next = { ...task };
  OPTIONAL_TEXT_TASK_FIELDS.forEach((field) => {
    if (typeof next[field] === 'string' && next[field]?.trim() === '') {
      delete next[field];
    }
  });
  return next;
};

const stripInvalidDueDateFields = (
  task: CreateCampaignTaskV2Input
): CreateCampaignTaskV2Input => {
  const next = { ...task } as CreateCampaignTaskV2Input & Record<string, any>;

  if (next.contentDueDateType === CbDueDateType.APPLICATION_BASED) {
    if (!next.contentValueInterval || !next.contentCbDisplayPeriod) {
      delete next.contentDueDateType;
      delete next.contentValueInterval;
      delete next.contentCbDisplayPeriod;
    }
  } else if (next.contentDueDateType === CbDueDateType.FIXED) {
    if (!next.contentFixedDate) {
      delete next.contentDueDateType;
      delete next.contentFixedDate;
    }
  } else if (!next.contentDueDateType) {
    delete next.contentDueDateType;
    delete next.contentFixedDate;
    delete next.contentValueInterval;
    delete next.contentCbDisplayPeriod;
  }

  if (
    next.postDueDateType === CbDueDateType.CONTENT_BASED ||
    next.postDueDateType === CbDueDateType.APPLICATION_BASED
  ) {
    if (!next.postValueInterval || !next.postDisplayPeriod) {
      delete next.postDueDateType;
      delete next.postValueInterval;
      delete next.postDisplayPeriod;
    }
  } else if (next.postDueDateType === CbDueDateType.FIXED) {
    if (!next.postFixedDate || !next.postFixedDateType) {
      delete next.postDueDateType;
      delete next.postFixedDate;
      delete next.postFixedDateType;
    }
  } else if (next.postDueDateType === CbDueDateType.RANGE) {
    if (!next.postRangeFrom || !next.postRangeTo) {
      delete next.postDueDateType;
      delete next.postRangeFrom;
      delete next.postRangeTo;
    }
  } else if (!next.postDueDateType) {
    delete next.postDueDateType;
    delete next.postFixedDate;
    delete next.postFixedDateType;
    delete next.postRangeFrom;
    delete next.postRangeTo;
    delete next.postValueInterval;
    delete next.postDisplayPeriod;
  }

  Object.keys(next).forEach((key) => {
    if (next[key] === undefined || next[key] === '') {
      delete next[key];
    }
  });

  return next;
};

type BuildCustomActivityArgs = {
  values: CustomCampaignFormValues;
  activity: GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity | null;
  deliverablesChecklist: CustomCampaignDeliverable[];
  contracts: { key: string }[];
};

export const buildCustomActivityInput = ({
  values,
  activity,
  deliverablesChecklist,
  contracts,
}: BuildCustomActivityArgs): CustomBriefActivityDetailsInput => {
  const promoValue = values.promoValue?.trim() || '';
  const productSource = values.products ?? activity?.products ?? [];
  const products = productSource
    .filter((product) => product.name?.trim())
    .map((product) => ({
      name: product.name,
      description: product.description || null,
      cost: toNullableNumber(product.cost) ?? 0,
      qty: toNullableNumber(product.qty) ?? 1,
      shipping: Boolean(product.shipping),
    }));

  return {
    ...(values.instructions && { instructions: values.instructions }),
    ...(values.brandName && { brandName: values.brandName }),
    ...(values.website && { website: values.website }),
    ...(values.encouraged && { encouraged: values.encouraged }),
    ...(values.avoid && { avoid: values.avoid }),
    products,
    promoCodes:
      promoValue && values.promoType !== PromoType.Link ? [promoValue] : [],
    promoUrls:
      promoValue && values.promoType === PromoType.Link ? [promoValue] : [],
    contracts,
    tasksV2: transformCustomTasks(deliverablesChecklist),
  };
};

export const getExpectedApprovals = (
  budget: unknown,
  pricePerAthlete: unknown
): number => {
  const budgetValue = toNullableNumber(budget);
  const priceValue = toNullableNumber(pricePerAthlete);
  if (!budgetValue || !priceValue || budgetValue < 0 || priceValue < 0) {
    return 0;
  }
  return Math.floor(budgetValue / priceValue);
};

export type BuildUpdateInputArgs = {
  record: GetCampaignsV2_getCampaigns_entities;
  values: CustomCampaignFormValues;
  activity: GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity | null;
  deliverablesChecklist: CustomCampaignDeliverable[];
  contracts: { key: string }[];
  platformCriteria: PlatformCriteriaItem[];
  talentId?: string | null;
  /** Preselected talent ids for custom campaigns created from My Talent. */
  talentIds?: string[] | null;
  /** True when the admin edited Budget or the Reserve switch in this session. */
  budgetTouched?: boolean;
};

// Private/invite-only campaigns don't search for athletes (talent is preselected
// via talentIds), so the full criteria is not needed — only flatFeePerStore must
// be persisted. Sending a minimal criteria avoids the nested validators
// (e.g. @ArrayMinSize(1)) the public path requires.
export const buildPrivateCustomCriteriaInput = ({
  values,
}: {
  values: CustomCampaignFormValues;
}): SearchCustomCriteriaInput => ({
  flatFeePerStore: toNullableNumber(values.flatFeePerStore),
});

export const buildUpdateCustomCampaignInput = ({
  record,
  values,
  activity,
  deliverablesChecklist,
  contracts,
  platformCriteria,
  talentIds,
  budgetTouched = false,
}: BuildUpdateInputArgs): UpdateCustomCampaignInput => {
  const budget = toNullableNumber(values.budget);
  const shouldSendReservedBudget =
    budgetTouched && !(values.reserveBudget && budget == null);
  const preselectedTalentIds = talentIds?.filter(Boolean) ?? [];
  const hasPreselectedTalent = preselectedTalentIds.length > 0;
  // Visibility comes from the form/record only — inferring private from the talent
  // list converted public on-review campaigns (which already carry search-generated
  // offers) into private ones and wiped their criteria.
  const isPrivate = values.isPrivate;

  return {
    id: record.id,
    lockEnabled: true,
    name: values.name,
    internalName: values.internalName || values.name,
    isPrivate,
    activities: [
      {
        type: CampaignActivityType.CustomBrief,
        customBrief: buildCustomActivityInput({
          values,
          activity,
          deliverablesChecklist,
          contracts,
        }),
      },
    ],
    questions: values.questions
      ?.filter(Boolean)
      .map((question) => ({ question, isRequired: true })),
    usageRights: !values.usageRights,
    ...(values.usageRightsText && { usageRightsText: values.usageRightsText }),
    ...(values.usageRightsDurationMonths != null && {
      usageRightsDurationMonths: values.usageRightsDurationMonths,
    }),
    ...(values.usageRightsScopes?.length && {
      usageRightsScopes: values.usageRightsScopes,
    }),
    ...(values.applicationDeadlineDays != null && {
      applicationDeadlineDays: values.applicationDeadlineDays,
    }),
    ...(hasPreselectedTalent && { talentIds: preselectedTalentIds }),
    expectedBudget: budget,
    ...(shouldSendReservedBudget && {
      reservedBudget: values.reserveBudget ? budget : null,
    }),
    // Public campaigns send the full criteria so platformCriteria / filters are
    // preserved and offers can be regenerated. Private campaigns still need the
    // price persisted (approval reprices the roster from campaign.flatFeePerStore),
    // so they send the minimal criteria instead of none.
    criteria: isPrivate
      ? buildPrivateCustomCriteriaInput({ values })
      : buildCustomCriteriaInput({ values, platformCriteria }),
  };
};

export const buildUpdateDirectBookingCampaignInput = ({
  record,
  values,
  activity,
  deliverablesChecklist,
  contracts,
  talentId,
}: BuildUpdateInputArgs): UpdateDirectBookingCampaignInput => {
  return {
    id: record.id,
    lockEnabled: true,
    name: values.name,
    internalName: values.internalName || values.name,
    ...(talentId && { talentId }),
    flatFeePerStore: toNullableNumber(values.flatFeePerStore),
    activities: [
      {
        type: CampaignActivityType.CustomBrief,
        customBrief: buildCustomActivityInput({
          values,
          activity,
          deliverablesChecklist,
          contracts,
        }),
      },
    ],
    usageRights: !values.usageRights,
    ...(values.usageRightsText && { usageRightsText: values.usageRightsText }),
    ...(values.usageRightsDurationMonths != null && {
      usageRightsDurationMonths: values.usageRightsDurationMonths,
    }),
    ...(values.usageRightsScopes?.length && {
      usageRightsScopes: values.usageRightsScopes,
    }),
  };
};
